import Dexie, { type Table } from 'dexie';
import { KEY_DB_NAME } from './constants';
import type { DerivedKeys } from './keys';

/**
 * Persistence of the derived keys on this device. The AES and HMAC keys are stored as
 * non-extractable `CryptoKey` objects: scripts of the page can use them but cannot read their bytes.
 * The write token is a string and is stored as is, because it must be sent in a request header;
 * anyone who can read this device's IndexedDB can therefore push to (and delete) the space, but
 * cannot decrypt anything. The passphrase itself is never stored.
 */
export interface KeyStore {
  save(keys: DerivedKeys): Promise<void>;
  load(): Promise<DerivedKeys | null>;
  /** Forget the keys and the account session. */
  clear(): Promise<void>;
  /** The account session token (a bearer secret for the account endpoints only); `null` when none. */
  loadSession(): Promise<string | null>;
  saveSession(token: string): Promise<void>;
  /** Forget the session token only. */
  clearSession(): Promise<void>;
}

const CURRENT = 'current';
const SESSION = 'session';

interface SessionRow {
  readonly id: string;
  readonly token: string;
}

interface KeyRow {
  readonly id: string;
  readonly spaceId: string;
  readonly authToken: string;
  readonly encKey: CryptoKey;
  readonly ridKey: CryptoKey;
}

class KeyDb extends Dexie {
  keys!: Table<KeyRow | SessionRow, string>;
  constructor(name: string) {
    super(name);
    this.version(1).stores({ keys: 'id' });
  }
}

function isKeyRow(row: unknown): row is KeyRow {
  if (typeof row !== 'object' || row === null) return false;
  const r = row as Record<string, unknown>;
  return (
    typeof r.spaceId === 'string' &&
    typeof r.authToken === 'string' &&
    r.encKey instanceof CryptoKey &&
    r.ridKey instanceof CryptoKey
  );
}

/** Keys in the dedicated IndexedDB database `wiztime-keys`. */
export function createIndexedDbKeyStore(dbName: string = KEY_DB_NAME): KeyStore {
  let db: KeyDb | null = null;
  const open = (): KeyDb => (db ??= new KeyDb(dbName));
  return {
    async save(keys) {
      const row: KeyRow = {
        id: CURRENT,
        spaceId: keys.spaceId,
        authToken: keys.authToken,
        encKey: keys.encKey,
        ridKey: keys.ridKey,
      };
      await open().keys.put(row);
    },
    async load() {
      const row: unknown = await open().keys.get(CURRENT);
      if (!isKeyRow(row)) return null;
      return { spaceId: row.spaceId, authToken: row.authToken, encKey: row.encKey, ridKey: row.ridKey };
    },
    async clear() {
      await open().keys.clear();
    },
    async loadSession() {
      const row: unknown = await open().keys.get(SESSION);
      if (typeof row !== 'object' || row === null) return null;
      const token = (row as Record<string, unknown>).token;
      return typeof token === 'string' && token !== '' ? token : null;
    },
    async saveSession(token) {
      await open().keys.put({ id: SESSION, token });
    },
    async clearSession() {
      await open().keys.delete(SESSION);
    },
  };
}

export function createMemoryKeyStore(initial: DerivedKeys | null = null): KeyStore {
  let current = initial;
  let session: string | null = null;
  return {
    save: async (keys) => {
      current = keys;
    },
    load: async () => current,
    clear: async () => {
      current = null;
      session = null;
    },
    loadSession: async () => session,
    saveSession: async (token) => {
      session = token;
    },
    clearSession: async () => {
      session = null;
    },
  };
}
